import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Badge } from '../components/ui/badge';
import { BookOpen, Clock, ArrowRight, TrendingUp } from 'lucide-react';
import { Skeleton } from '../components/ui/skeleton';

function BlogPage() {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/blogs`);
      const data = await response.json();
      setArticles(data.blogs || []);
    } catch (error) {
      console.error('Failed to fetch blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = ['all', ...new Set(articles.map(a => a.category).filter(Boolean))];

  const filteredArticles = articles.filter(article => {
    return selectedCategory === 'all' || article.category === selectedCategory;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-background pb-20 md:pb-8">
        <div className="container mx-auto px-4 py-6 max-w-4xl">
          <Skeleton className="h-12 w-64 mb-4" />
          <Skeleton className="h-6 w-full mb-8" />
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-48 w-full mb-4" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-8">
      <Helmet>
        <title>Blog & Parenting Tips - Mothering</title>
        <meta name="description" content="Expert advice on parenting, kids products, and child development" />
      </Helmet>

      {/* Minimal Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
        <div className="container mx-auto px-4 py-4 max-w-4xl">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-cyan-600" />
              Blog
            </h1>
            <TrendingUp className="w-5 h-5 text-muted-foreground" />
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Category Pills */}
        <div className="flex gap-2 overflow-x-auto pb-4 mb-6 -mx-4 px-4 scrollbar-hide">
          {categories.map((cat) => (
            <Badge
              key={cat}
              variant={selectedCategory === cat ? 'default' : 'outline'}
              className={`cursor-pointer whitespace-nowrap px-4 py-2 text-sm ${
                selectedCategory === cat
                  ? 'bg-cyan-600 hover:bg-cyan-700 text-white'
                  : 'hover:bg-cyan-50 hover:border-cyan-300'
              }`}
              onClick={() => setSelectedCategory(cat)}
              data-testid={`category-${cat}`}
            >
              {cat === 'all' ? 'All' : cat.charAt(0).toUpperCase() + cat.slice(1)}
            </Badge>
          ))}
        </div>

        {/* Articles List - Clean, Card-based */}
        <div className="space-y-4">
          {filteredArticles.length > 0 ? (
            filteredArticles.map((article, index) => (
              <article
                key={article.id}
                onClick={() => navigate(`/blog/${article.id}`)}
                className="bg-card rounded-xl overflow-hidden border hover:shadow-lg transition-all duration-200 cursor-pointer active:scale-[0.98]"
                data-testid="blog-article-card"
              >
                {/* Image */}
                {article.featured_image && (
                  <div className="aspect-[2/1] overflow-hidden bg-muted">
                    <img
                      src={article.featured_image}
                      alt={article.title}
                      className="w-full h-full object-cover"
                      loading={index < 2 ? 'eager' : 'lazy'}
                    />
                  </div>
                )}

                {/* Content */}
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    {article.category && (
                      <Badge className="bg-cyan-600 text-xs font-medium">
                        {article.category}
                      </Badge>
                    )}
                    {article.reading_time && (
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {article.reading_time} min
                      </span>
                    )}
                  </div>

                  <h2 className="text-lg font-bold mb-2 line-clamp-2 leading-snug">
                    {article.title}
                  </h2>

                  {article.excerpt && (
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                      {article.excerpt}
                    </p>
                  )}

                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{article.author || 'Mothering Team'}</span>
                    <ArrowRight className="w-4 h-4 text-cyan-600" />
                  </div>
                </div>
              </article>
            ))
          ) : (
            <div className="text-center py-16">
              <BookOpen className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-muted-foreground">No articles found</p>
            </div>
          )}
        </div>
      </div>

      {/* Bottom spacing for mobile nav */}
      <div className="h-20 md:hidden" />
    </div>
  );
}

export default BlogPage;
